import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Icon,
  IconButton,
  SimpleGrid,
  Text,
  Tooltip,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { ArrowRight, ShieldCheck, Trash2 } from 'lucide-react';
import { Column } from 'react-table';
import { useHistory } from 'react-router-dom';
import {
  OktonReactTable as Table,
  ITableDataProps,
} from '@okton/platform-ui/react-table';

import Page from '../../layout/IntegraAppPage';
import { useAuth } from '../../hooks/auth';
import { useTokensIntegra } from '../../services/hooks/useTokensIntegra';

type TokenSelecionado = {
  id: number;
  nome: string;
};

const formatarDataToken = (valor?: string | null) => {
  if (!valor) return '-';

  const data = new Date(valor);

  if (Number.isNaN(data.getTime())) return '-';

  return data.toLocaleDateString('pt-BR');
};

const TokenIntegra: React.FC = () => {
  const history = useHistory();
  const { user } = useAuth();
  const toast = useToast();

  const {
    isOpen: confirmacaoAberta,
    onOpen: abrirConfirmacao,
    onClose: fecharConfirmacao,
  } = useDisclosure();

  const botaoCancelarRef = useRef<HTMLButtonElement>(null);

  const pageBg = useColorModeValue('#EEF5F9', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');

  const modalBg = useColorModeValue('white', 'gray.800');
  const modalText = useColorModeValue('gray.800', 'gray.100');
  const modalMuted = useColorModeValue('gray.600', 'gray.400');
  const modalBorder = useColorModeValue('gray.200', 'gray.600');
  const modalFooterBg = useColorModeValue('gray.50', 'gray.700');

  const [tokenSelecionado, setTokenSelecionado] =
    useState<TokenSelecionado | null>(null);

  const { tokens, carregandoTokens, revogandoTokenId, revogarToken } =
    useTokensIntegra({
      empresaId: user?.empresa_id,
      filialId: user?.filial_id,
    });

  const solicitarRevogacao = useCallback(
    (id: number, nome: string) => {
      setTokenSelecionado({
        id,
        nome,
      });

      abrirConfirmacao();
    },
    [abrirConfirmacao],
  );

  const cancelarRevogacao = () => {
    if (revogandoTokenId !== null) return;

    fecharConfirmacao();
    setTokenSelecionado(null);
  };

  const confirmarRevogacao = async () => {
    if (!tokenSelecionado) return;

    try {
      await revogarToken({
        tokenId: tokenSelecionado.id,
      });

      toast({
        title: 'Token revogado com sucesso.',
        description: 'A credencial não poderá mais acessar a API.',
        status: 'success',
        position: 'bottom-right',
        isClosable: true,
      });

      fecharConfirmacao();
      setTokenSelecionado(null);
    } catch {
      toast({
        title: 'Não foi possível revogar o token.',
        description: 'Tente novamente em alguns instantes.',
        status: 'error',
        position: 'bottom-right',
        isClosable: true,
      });
    }
  };

  const colunas = useMemo<Array<Column<ITableDataProps>>>(
    () => [
      {
        Header: 'TOKEN',
        accessor: 'nome',
      },
      {
        Header: 'PERMISSÕES',
        accessor: 'permissoes',
        Cell: ({ value }: { value: React.ReactNode }) => value,
      },
      {
        Header: 'CRIADO EM',
        accessor: 'criado_em',
      },
      {
        Header: 'EXPIRA EM',
        accessor: 'expira_em',
      },
      {
        Header: 'ÚLTIMO USO',
        accessor: 'ultimo_uso',
      },
      {
        Header: 'STATUS',
        accessor: 'status',
        Cell: ({ value }: { value: React.ReactNode }) => value,
      },
      {
        Header: 'AÇÕES',
        accessor: 'actions',
        Cell: ({ value }: { value: React.ReactNode }) => value,
      },
    ],
    [],
  );

  const dadosTabela = useMemo<ITableDataProps[]>(
    () =>
      tokens.map(token => {
        const nome = token.nome || token.name || `Token #${token.id}`;

        return {
          nome,
          permissoes: (
            <HStack spacing={2} flexWrap="wrap">
              {(token.abilities || []).map(ability => (
                <Badge key={ability} colorScheme="blue" textTransform="none">
                  {ability}
                </Badge>
              ))}
            </HStack>
          ),
          criado_em: formatarDataToken(token.created_at),
          expira_em: formatarDataToken(token.expires_at),
          ultimo_uso: formatarDataToken(token.last_used_at),
          status: (
            <Badge colorScheme={token.expirado ? 'red' : 'green'}>
              {token.expirado ? 'Expirado' : 'Ativo'}
            </Badge>
          ),
          actions: (
            <Tooltip label="Revogar token">
              <IconButton
                aria-label={`Revogar ${nome}`}
                icon={<Trash2 size={16} />}
                variant="ghost"
                colorScheme="red"
                isLoading={revogandoTokenId === token.id}
                isDisabled={
                  revogandoTokenId !== null && revogandoTokenId !== token.id
                }
                onClick={event => {
                  event.stopPropagation();
                  solicitarRevogacao(token.id, nome);
                }}
              />
            </Tooltip>
          ),
        };
      }),
    [tokens, revogandoTokenId, solicitarRevogacao],
  );

  return (
    <Page>
      <Box bg={pageBg} minH="100vh" p={{ base: 4, md: 6 }} color={textColor}>
        <Flex
          justify="space-between"
          align={{ base: 'stretch', md: 'center' }}
          direction={{ base: 'column', md: 'row' }}
          mb={6}
          gap={4}
        >
          <Box>
            <Heading fontWeight="bold" fontSize={25}>
              API Integra
            </Heading>

            <Text fontSize="sm" color={mutedColor}>
              Gerencie os tokens gerados para integrações externas.
            </Text>
          </Box>

          <Button
            colorScheme="blue"
            rightIcon={<ArrowRight size={16} />}
            onClick={() => history.push('/tokens/integra/novo')}
          >
            Gerar token
          </Button>
        </Flex>

        <SimpleGrid columns={{ base: 1, xl: 3 }} spacing={5} mb={6}>
          <Box
            bg={cardBg}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="lg"
            p={5}
          >
            <HStack justify="space-between">
              <Box>
                <Text fontSize="xs" color={mutedColor}>
                  Tokens cadastrados
                </Text>

                <Text mt={2} fontWeight="bold" fontSize="2xl">
                  {tokens.length}
                </Text>
              </Box>

              <Icon as={ShieldCheck} color="blue.500" boxSize={6} />
            </HStack>
          </Box>

          <Box
            bg={cardBg}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="lg"
            p={5}
          >
            <Text fontSize="xs" color={mutedColor}>
              Empresa
            </Text>

            <Text mt={2} fontWeight="bold">
              #{user?.empresa_id || '-'}
            </Text>
          </Box>

          <Box
            bg={cardBg}
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="lg"
            p={5}
          >
            <Text fontSize="xs" color={mutedColor}>
              Filial
            </Text>

            <Text mt={2} fontWeight="bold">
              #{user?.filial_id || '-'}
            </Text>
          </Box>
        </SimpleGrid>

        <Box
          bg={cardBg}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="lg"
          p={4}
          overflowX="auto"
        >
          <Table
            columns={colunas}
            data={dadosTabela}
            isLoading={carregandoTokens}
            tableLength={tokens.length}
          />
        </Box>
      </Box>

      <AlertDialog
        isOpen={confirmacaoAberta}
        leastDestructiveRef={botaoCancelarRef}
        onClose={cancelarRevogacao}
        isCentered
      >
        <AlertDialogOverlay bg="blackAlpha.600">
          <AlertDialogContent
            bg={modalBg}
            color={modalText}
            borderWidth="1px"
            borderColor={modalBorder}
            borderRadius="xl"
            overflow="hidden"
            mx={4}
          >
            <AlertDialogHeader
              fontSize="lg"
              fontWeight="bold"
              color={modalText}
              borderBottomWidth="1px"
              borderBottomColor={modalBorder}
              px={6}
              py={4}
            >
              Revogar token
            </AlertDialogHeader>

            <AlertDialogBody px={6} py={5}>
              <Text color={modalText}>
                Tem certeza que deseja revogar o token{' '}
                <Text as="span" fontWeight="bold" color={modalText}>
                  {tokenSelecionado?.nome}
                </Text>
                ?
              </Text>

              <Text mt={3} fontSize="sm" color={modalMuted}>
                Sistemas que utilizam essa credencial perderão o acesso
                imediatamente. Essa ação não poderá ser desfeita.
              </Text>
            </AlertDialogBody>

            <AlertDialogFooter
              bg={modalFooterBg}
              borderTopWidth="1px"
              borderTopColor={modalBorder}
              px={6}
              py={4}
            >
              <Button
                ref={botaoCancelarRef}
                onClick={cancelarRevogacao}
                isDisabled={revogandoTokenId !== null}
                variant="outline"
                borderRadius="lg"
              >
                Cancelar
              </Button>

              <Button
                colorScheme="red"
                ml={3}
                onClick={confirmarRevogacao}
                isLoading={tokenSelecionado?.id === revogandoTokenId}
                loadingText="Revogando"
                borderRadius="lg"
              >
                Revogar token
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Page>
  );
};

export default TokenIntegra;
